import { Dispatch, SetStateAction } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import {
  BackButton,
  BottomSection,
  Button,
  DropdownButton,
  TitleSection,
} from '../../styled.components'
import styled from '@emotion/styled'
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Textarea,
} from '@nextui-org/react-latest'
import { IoMdArrowDropdown } from 'react-icons/io'
import { useForm } from 'react-hook-form'
import { colors } from '@configs/styles/config'

type InquiryType =
  | ''
  | 'introductionToEdugramFeatures'
  | 'accountSetupAndVerification'
  | 'contentCreationAndPublishingGuidelines'
  | 'earningOpportunitiesAndRevenueModels'
  | 'technicalSupportAndTroubleshooting'
  | 'feedbackAndImprovementSuggestions'
  | 'generalInquiry'
  | 'other'

type Props = {
  setCurrentStep: Dispatch<
    SetStateAction<
      'stepone' | 'steptwo' | 'stepthree' | 'stepfour' | 'stepfive'
    >
  >
  setInquiryType: Dispatch<SetStateAction<InquiryType>>
  inquiryType: InquiryType
  setInquiryDetails: Dispatch<SetStateAction<string>>
  inquiryDetails: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.5rem;
  max-width: 340px;
  margin: 2rem auto;
  margin-top: 0rem;
`

const MiddleSection = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
  width: 100%;
`
const ErrorMessageWrapper = styled.div`
  font-size: 14px;
  color: ${colors.uguRed};
  font-family: var(--font-inter);
`

const StepThree = ({
  setCurrentStep,
  inquiryType,
  setInquiryType,
  setInquiryDetails,
}: Props) => {
  const { t } = useTranslation(['landingPage'])
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<{ inquiryDetails: string }>({ mode: 'onChange' })
  const options = [
    {
      value: 'introductionToEdugramFeatures',
      label: t('connectWithUs.stepThree.options.introductionToEdugramFeatures'),
    },
    {
      value: 'accountSetupAndVerification',
      label: t('connectWithUs.stepThree.options.accountSetupAndVerification'),
    },
    {
      value: 'contentCreationAndPublishingGuidelines',
      label: t(
        'connectWithUs.stepThree.options.contentCreationAndPublishingGuidelines'
      ),
    },
    {
      value: 'earningOpportunitiesAndRevenueModels',
      label: t(
        'connectWithUs.stepThree.options.earningOpportunitiesAndRevenueModels'
      ),
    },
    {
      value: 'technicalSupportAndTroubleshooting',
      label: t(
        'connectWithUs.stepThree.options.technicalSupportAndTroubleshooting'
      ),
    },
    {
      value: 'feedbackAndImprovementSuggestions',
      label: t(
        'connectWithUs.stepThree.options.feedbackAndImprovementSuggestions'
      ),
    },
    {
      value: 'generalInquiry',
      label: t('connectWithUs.stepThree.options.generalInquiry'),
    },
    { value: 'other', label: t('connectWithUs.stepThree.options.other') },
  ]

  const handleChangeSelection = (key: { currentKey: InquiryType }) => {
    setInquiryType(key.currentKey)
  }

  const onSubmit = (data: { inquiryDetails: string }) => {
    if (data.inquiryDetails.trim().length < 50) {
      return setError('inquiryDetails', {
        message: t('connectWithUs.stepThree.errors.minLength'),
      })
    }
    setInquiryDetails(data.inquiryDetails)
    setCurrentStep('stepfour')
  }

  return (
    <motion.div
      initial={{ opacity: 0, translateY: 100 }}
      animate={{
        opacity: 1,
        translateY: 0,
      }}
      transition={{
        duration: 0.3,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <TitleSection>{t('connectWithUs.stepThree.title')}</TitleSection>
          <MiddleSection>
            <Dropdown shouldBlockScroll={false}>
              <DropdownTrigger>
                <DropdownButton>
                  <span className="max-w-[95%]">
                    {inquiryType
                      ? options
                          .find((op) => op.value === inquiryType)
                          ?.label?.substring(0, 33)
                      : t('connectWithUs.stepThree.selectPlaceholder')}
                  </span>
                  <IoMdArrowDropdown />
                </DropdownButton>
              </DropdownTrigger>
              <DropdownMenu
                //@ts-ignore
                onSelectionChange={handleChangeSelection}
                color="primary"
                selectionMode="single"
              >
                {options.map((item) => (
                  <DropdownItem className="font-[Inter]" key={item.value}>
                    {item.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <div className="flex flex-col">
              <Textarea
                variant="bordered"
                className="font-[Inter] tracking-normal"
                placeholder={t('connectWithUs.stepThree.textareaPlaceholder')}
                classNames={{
                  // inputWrapper: '!min-h-[130px]',
                  input: 'text-[15px]',
                }}
                {...register('inquiryDetails', {
                  required: t('connectWithUs.stepThree.errors.required'),
                  minLength: {
                    value: 50,
                    message: t('connectWithUs.stepThree.errors.minLength'),
                  },
                  maxLength: {
                    value: 400,
                    message: t('connectWithUs.stepThree.errors.maxLength'),
                  },
                })}
                isInvalid={errors?.inquiryDetails ? true : false}
              />
              {errors.inquiryDetails && (
                <ErrorMessageWrapper>
                  {errors.inquiryDetails.message}
                </ErrorMessageWrapper>
              )}
            </div>
          </MiddleSection>
          <BottomSection>
            <BackButton onClick={() => setCurrentStep('steptwo')}>
              {t('connectWithUs.stepThree.goBack')}
            </BackButton>
            <Button
              type={'submit'}
              disabled={!inquiryType}
              // onClick={() => setCurrentStep('stepfour')}
            >
              {t('connectWithUs.stepThree.nextStep')}
            </Button>
          </BottomSection>
        </Container>
      </form>
    </motion.div>
  )
}

export default StepThree
